<!-- src/components/WeatherStrip.vue -->

<template>
  <div class="weather-strip">
    <div v-if="loading" class="weather-strip__status">Loading weather...</div>

    <div
      v-else-if="error"
      class="weather-strip__status weather-strip__status--error"
      :title="error"
    >
      Weather unavailable
    </div>

    <div v-else-if="forecast.length" class="weather-strip__days">
      <div v-for="day in forecast" :key="day.date" class="weather-day" :title="day.title">
        <div class="weather-day__name">
          {{ day.day }}
        </div>

        <div class="weather-day__icon" aria-hidden="true">
          {{ day.icon }}
        </div>

        <div class="weather-day__temp">
          <span class="weather-day__high"> {{ day.high }}° </span>

          <span class="weather-day__low"> {{ day.low }}° </span>
        </div>

        <div v-if="day.rain > 0" class="weather-day__rain">{{ day.rain }}%</div>

        <div v-else class="weather-day__rain weather-day__rain--empty">&nbsp;</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";

import { doc, getDoc } from "firebase/firestore";

import { db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

const { venueId } = useVenueContext();

const forecast = ref([]);

const loading = ref(false);

const error = ref("");

let requestNumber = 0;

const CACHE_DURATION = 60 * 60 * 1000;

/* =========================================================
   HELPERS
   ========================================================= */

function clean(value) {
  return String(value ?? "").trim();
}

function asNumber(value) {
  const number = Number(value);

  return Number.isFinite(number) ? number : null;
}

function cacheKey(id) {
  return `coredeck_weather_${id}`;
}

function roundTemperature(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "-";
  }

  return Math.round(number);
}

function dayLabel(isoDate, index) {
  if (index === 0) {
    return "Today";
  }

  const date = new Date(`${isoDate}T12:00:00`);

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
  }).format(date);
}

/* =========================================================
   WEATHER CODE
   Open-Meteo uses WMO weather interpretation codes.
   ========================================================= */

function weatherDetails(codeValue) {
  const code = Number(codeValue);

  if (code === 0) {
    return {
      icon: "☀️",

      description: "Clear sky",
    };
  }

  if (code === 1) {
    return {
      icon: "🌤️",

      description: "Mainly clear",
    };
  }

  if (code === 2) {
    return {
      icon: "⛅",

      description: "Partly cloudy",
    };
  }

  if (code === 3) {
    return {
      icon: "☁️",

      description: "Overcast",
    };
  }

  if (code === 45 || code === 48) {
    return {
      icon: "🌫️",

      description: "Fog",
    };
  }

  if (code === 51 || code === 53 || code === 55) {
    return {
      icon: "🌦️",

      description: "Drizzle",
    };
  }

  if (code === 56 || code === 57) {
    return {
      icon: "🌧️",

      description: "Freezing drizzle",
    };
  }

  if (code === 61 || code === 63 || code === 65) {
    return {
      icon: "🌧️",

      description: "Rain",
    };
  }

  if (code === 66 || code === 67) {
    return {
      icon: "🌧️",

      description: "Freezing rain",
    };
  }

  if (code === 71 || code === 73 || code === 75 || code === 77) {
    return {
      icon: "❄️",

      description: "Snow",
    };
  }

  if (code === 80 || code === 81 || code === 82) {
    return {
      icon: "🌦️",

      description: "Rain showers",
    };
  }

  if (code === 85 || code === 86) {
    return {
      icon: "🌨️",

      description: "Snow showers",
    };
  }

  if (code === 95) {
    return {
      icon: "⛈️",

      description: "Thunderstorm",
    };
  }

  if (code === 96 || code === 99) {
    return {
      icon: "⛈️",

      description: "Thunderstorm with hail",
    };
  }

  return {
    icon: "🌤️",

    description: "Weather",
  };
}

/* =========================================================
   CACHE
   ========================================================= */

function readCache(id) {
  try {
    const raw = localStorage.getItem(cacheKey(id));

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);

    if (!parsed || !Array.isArray(parsed.forecast) || !parsed.savedAt) {
      return null;
    }

    if (Date.now() - Number(parsed.savedAt) > CACHE_DURATION) {
      localStorage.removeItem(cacheKey(id));

      return null;
    }

    return parsed.forecast;
  } catch {
    return null;
  }
}

function saveCache(id, data) {
  try {
    localStorage.setItem(
      cacheKey(id),

      JSON.stringify({
        savedAt: Date.now(),

        forecast: data,
      })
    );
  } catch {
    // cache is optional
  }
}

/* =========================================================
   VENUE LOCATION
   ========================================================= */

async function getVenueLocation(id) {
  const snapshot = await getDoc(doc(db, "venues", id));

  if (!snapshot.exists()) {
    throw new Error(`Venue ${id} does not exist.`);
  }

  const data = snapshot.data() || {};

  const latitude = asNumber(data.latitude ?? data.lat);

  const longitude = asNumber(data.longitude ?? data.lng ?? data.lon);

  if (latitude === null || longitude === null) {
    throw new Error(`Venue ${id} does not have latitude and longitude configured.`);
  }

  return {
    latitude,
    longitude,
  };
}

/* =========================================================
   OPEN-METEO
   ========================================================= */

async function fetchForecast(latitude, longitude) {
  const params = new URLSearchParams({
    latitude: String(latitude),

    longitude: String(longitude),

    daily: [
      "weather_code",
      "temperature_2m_max",
      "temperature_2m_min",
      "precipitation_probability_max",
    ].join(","),

    timezone: "auto",

    forecast_days: "7",
  });

  const response = await fetch(
    `https://api.open-meteo.com/v1/forecast?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error(`Weather request failed with HTTP ${response.status}.`);
  }

  const data = await response.json();

  const daily = data?.daily;

  if (!daily || !Array.isArray(daily.time)) {
    throw new Error("Weather forecast did not contain daily data.");
  }

  return daily.time.slice(0, 7).map((date, index) => {
    const details = weatherDetails(daily.weather_code?.[index]);

    const high = roundTemperature(daily.temperature_2m_max?.[index]);

    const low = roundTemperature(daily.temperature_2m_min?.[index]);

    const rain = Math.max(
      0,

      Math.round(Number(daily.precipitation_probability_max?.[index] ?? 0))
    );

    return {
      date,

      day: dayLabel(date, index),

      icon: details.icon,

      description: details.description,

      high,

      low,

      rain,

      title: `${details.description} · High ${high}° · Low ${low}° · Rain ${rain}%`,
    };
  });
}

/* =========================================================
   LOAD
   ========================================================= */

async function loadWeather(force = false) {
  const id = clean(venueId.value);

  const thisRequest = ++requestNumber;

  forecast.value = [];

  error.value = "";

  if (!id) {
    return;
  }

  if (!force) {
    const cached = readCache(id);

    if (cached?.length) {
      forecast.value = cached;

      return;
    }
  }

  loading.value = true;

  try {
    const { latitude, longitude } = await getVenueLocation(id);

    const result = await fetchForecast(latitude, longitude);

    if (thisRequest !== requestNumber) {
      return;
    }

    forecast.value = result;

    saveCache(id, result);
  } catch (loadError) {
    if (thisRequest !== requestNumber) {
      return;
    }

    console.warn("[WeatherStrip] Weather load failed:", loadError);

    error.value = loadError?.message || String(loadError);
  } finally {
    if (thisRequest === requestNumber) {
      loading.value = false;
    }
  }
}

/* =========================================================
   LIFECYCLE
   ========================================================= */

watch(
  venueId,

  () => {
    loadWeather();
  }
);

onMounted(() => {
  loadWeather();
});
</script>

<style scoped>
.weather-strip {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  max-width: 100%;

  display: flex;
  align-items: center;
}

.weather-strip__days {
  width: 100%;
  min-width: 0;

  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));

  align-items: center;

  gap: clamp(2px, 0.6vw, 10px);
}

.weather-day {
  width: 100%;
  min-width: 0;

  display: grid;

  grid-template-columns:
    20px
    minmax(0, auto)
    minmax(20px, auto);

  grid-template-rows:
    11px
    20px;

  column-gap: 2px;

  align-items: center;
  justify-content: center;

  text-align: center;

  line-height: 1;
}

.weather-day__name {
  grid-column: 1 / -1;
  grid-row: 1;

  margin: 0;

  color: #697278;

  font-size: clamp(7px, 0.55vw, 9px);

  font-weight: 600;

  text-transform: uppercase;

  letter-spacing: 0.03em;

  white-space: nowrap;
}

.weather-day__icon {
  grid-column: 1;
  grid-row: 2;

  width: 20px;
  height: 20px;

  display: flex;

  align-items: center;
  justify-content: center;

  font-size: clamp(13px, 1.1vw, 16px);

  line-height: 20px;
}

.weather-day__temp {
  grid-column: 2;
  grid-row: 2;

  min-width: 0;

  margin: 0;

  display: flex;

  align-items: center;
  justify-content: center;

  gap: 2px;

  font-size: clamp(8px, 0.65vw, 10px);

  white-space: nowrap;
}

.weather-day__high {
  color: #263238;

  font-weight: 700;
}

.weather-day__low {
  color: #879198;

  font-weight: 500;
}

.weather-day__rain {
  grid-column: 3;
  grid-row: 2;

  min-width: 0;
  min-height: 0;

  margin: 0;

  color: #1976d2;

  font-size: clamp(7px, 0.55vw, 8px);

  font-weight: 600;

  white-space: nowrap;
}

.weather-day__rain--empty {
  visibility: hidden;
}

.weather-strip__status {
  color: #879198;

  font-size: 11px;

  font-weight: 500;
}

.weather-strip__status--error {
  color: #9e9e9e;
}

/* Smaller desktop / tablet */
@media (max-width: 1100px) {
  .weather-strip__days {
    gap: 2px;
  }

  .weather-day {
    grid-template-columns:
      18px
      minmax(0, auto)
      minmax(18px, auto);

    column-gap: 1px;
  }

  .weather-day__icon {
    width: 18px;

    font-size: 13px;
  }
}

/* Mobile */
@media (max-width: 700px) {
  .weather-strip {
    width: 100%;
    max-width: 100%;

    overflow: hidden;
  }

  .weather-strip__days {
    width: 100%;

    grid-template-columns: repeat(7, minmax(0, 1fr));

    gap: 1px;
  }

  .weather-day {
    grid-template-columns: 1fr;
    grid-template-rows:
      10px
      18px
      12px;

    row-gap: 0;
  }

  .weather-day__name {
    grid-column: 1;
    grid-row: 1;

    font-size: 7px;
  }

  .weather-day__icon {
    grid-column: 1;
    grid-row: 2;

    width: 100%;

    font-size: 13px;
  }

  .weather-day__temp {
    grid-column: 1;
    grid-row: 3;

    font-size: 8px;
  }

  .weather-day__rain {
    display: none;
  }
}
</style>
